import { LAUNCH_DATE, LONDON_TZ } from './config';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** True for a real calendar date in YYYY-MM-DD form. */
export function isValidDateString(s: string): boolean {
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const [, y, mo, d] = m;
  const t = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  const back = new Date(t);
  return (
    back.getUTCFullYear() === Number(y) &&
    back.getUTCMonth() === Number(mo) - 1 &&
    back.getUTCDate() === Number(d)
  );
}

function assertDate(s: string): void {
  if (!isValidDateString(s)) throw new RangeError(`Invalid date string: ${s}`);
}

/** Midnight UTC of a YYYY-MM-DD string, as epoch ms. */
export function dateToUtcMs(s: string): number {
  assertDate(s);
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export function utcMsToDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addDays(date: string, days: number): string {
  return utcMsToDate(dateToUtcMs(date) + days * DAY_MS);
}

/** Whole days from `a` to `b` (negative if `b` is earlier). */
export function daysBetween(a: string, b: string): number {
  return Math.round((dateToUtcMs(b) - dateToUtcMs(a)) / DAY_MS);
}

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function londonParts(at: Date): Parts {
  const out: Partial<Parts> = {};
  for (const { type, value } of partsFormatter.formatToParts(at)) {
    if (type === 'year') out.year = Number(value);
    else if (type === 'month') out.month = Number(value);
    else if (type === 'day') out.day = Number(value);
    else if (type === 'hour') out.hour = Number(value) % 24;
    else if (type === 'minute') out.minute = Number(value);
    else if (type === 'second') out.second = Number(value);
  }
  return out as Parts;
}

/** The calendar date in London (YYYY-MM-DD) for an instant. Handles GMT/BST automatically. */
export function londonDateString(at: Date = new Date()): string {
  const p = londonParts(at);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** London's UTC offset in minutes at an instant (0 in winter, 60 in summer). */
export function londonOffsetMinutes(at: Date): number {
  const p = londonParts(at);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const truncated = Math.floor(at.getTime() / 1000) * 1000;
  return Math.round((asUtc - truncated) / 60_000);
}

/** The instant of the next 00:00 in London after `at`. */
export function nextLondonMidnight(at: Date = new Date()): Date {
  const tomorrow = addDays(londonDateString(at), 1);
  return londonMidnight(tomorrow);
}

/** The instant of 00:00 London time on a given London date. */
export function londonMidnight(date: string): Date {
  const guess = dateToUtcMs(date);
  // DST switches at 01:00 UTC, so the offset at 00:00 UTC on the same date is the one in force at London midnight.
  const offset = londonOffsetMinutes(new Date(guess));
  return new Date(guess - offset * 60_000);
}

export function msUntilNextRound(at: Date = new Date()): number {
  return nextLondonMidnight(at).getTime() - at.getTime();
}

/** Puzzle number = days since launch + 1 (PRD §4.1). */
export function puzzleNumber(date: string, launchDate: string = LAUNCH_DATE): number {
  return daysBetween(launchDate, date) + 1;
}

export function yesterdayOf(date: string): string {
  return addDays(date, -1);
}

/** Current year in London, the upper bound of the year slider. */
export function currentLondonYear(at: Date = new Date()): number {
  return londonParts(at).year;
}

/**
 * Leaderboards (global and group) cover today and `days` days back, but never a day before
 * puzzle #1 (`launch`).
 */
export function inLeaderboardWindow(
  date: string,
  today: string,
  launch: string,
  days: number,
): boolean {
  const age = daysBetween(date, today);
  return age >= 0 && age <= days && daysBetween(launch, date) >= 0;
}

/** Monday of the (Monday–Sunday) week that contains `date`. */
export function weekStart(date: string): string {
  const weekday = new Date(dateToUtcMs(date)).getUTCDay(); // 0 = Sunday
  return addDays(date, -((weekday + 6) % 7));
}
